'use client'

import {useCallback, useEffect, useRef} from 'react'
import {useAuth} from '@/lib/clerk-nextjs'

/**
 * TokenWarmup refreshes a Clerk session token on mount and
 * periodically while the tab is active, keeping the client
 * cache warm without persisting the token.
 */
export const TokenWarmup: React.FC = () => {
    const {getToken} = useAuth()
    const lastRefreshRef = useRef(0)
    const inFlightRef = useRef(false)

    const refreshToken = useCallback(async (force = false) => {
        if (!getToken || inFlightRef.current) return
        const now = Date.now()
        if (!force && now - lastRefreshRef.current < 4 * 60 * 1000) return
        inFlightRef.current = true
        try {
            await getToken(force ? {skipCache: true} : undefined)
            lastRefreshRef.current = Date.now()
        } catch {
            /* ignore warmup failures; normal flows will retry when needed */
        } finally {
            inFlightRef.current = false
        }
    }, [getToken])

    useEffect(() => {
        let canceled = false
        const schedule = () => {
            if (!canceled) {
                void refreshToken()
            }
        }
        setTimeout(schedule, 0)
        const interval = window.setInterval(() => {
            void refreshToken()
        }, 4 * 60 * 1000)
        const handleFocus = () => {
            void refreshToken(true)
        }
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                void refreshToken(true)
            }
        }
        window.addEventListener('focus', handleFocus)
        document.addEventListener('visibilitychange', handleVisibility)
        return () => {
            canceled = true
            window.clearInterval(interval)
            window.removeEventListener('focus', handleFocus)
            document.removeEventListener('visibilitychange', handleVisibility)
        }
    }, [refreshToken])

    return null
}
